import { useMemo } from 'react';
import { useGetList } from 'react-admin';
import type { Market, Merchant, MerchantRegion } from '../dataProvider';
import { useRateRuleCatalog } from '../rateRules/useRateRuleCatalog';
import type { RegionScopeView } from '../rateRules/rateRuleScope';

/**
 * The slug + market -> region-view index (D-page-schema-editor-8c-boost). The one new
 * piece of wiring: a preview store card references a brand SLUG, but the resolver
 * needs a merchant-region target, and the shared RegionScopeView carries no slug or
 * merchantId. So map slug + market -> the RegionScopeView, WITHOUT touching the shared
 * buildRegionViews / rateRuleScope.
 *
 * The path: merchant slug -> merchant id (merchants carry slug + id) -> the
 * merchantRegion row for (merchant id, MARKET) -> regionId -> the RegionScopeView from
 * useRateRuleCatalog. The MARKET matters: a slug is brand-level, a region is per-market,
 * so the same slug resolves to the uk region under home/uk and the us region under
 * home/us. The preview renders one market per column, so each column resolves its cards
 * against ITS market and the both-markets side-by-side correctness falls out.
 */
export const useStoreSlugRegionIndex = (): {
  resolve: (slug: string, market: Market) => RegionScopeView | null;
  isLoading: boolean;
} => {
  const big = { pagination: { page: 1, perPage: 1000 } } as const;
  const { views, isLoading: viewsLoading } = useRateRuleCatalog();
  const merchants = useGetList<Merchant>('merchants', big);
  const regions = useGetList<MerchantRegion>('merchantRegions', big);
  const isLoading = viewsLoading || merchants.isLoading || regions.isLoading;

  const index = useMemo(() => {
    const map = new Map<string, RegionScopeView>();
    if (isLoading) return map;
    const slugById = new Map((merchants.data ?? []).map((m) => [m.id, m.slug]));
    const viewById = new Map(views.map((v) => [v.regionId, v]));
    for (const r of regions.data ?? []) {
      const slug = slugById.get(r.merchantId);
      const view = viewById.get(r.id);
      if (slug != null && view) map.set(`${slug}:${r.market}`, view);
    }
    return map;
  }, [views, merchants.data, regions.data, isLoading]);

  return {
    resolve: (slug, market) => index.get(`${slug}:${market}`) ?? null,
    isLoading,
  };
};
